import React from 'react';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { MintingTypes } from '@soundxyz/utils/const';
import type { Maybe } from '@soundxyz/utils/helpers';
import { GetNotifiedButton } from '@components/EmailNotifications/GetNotifiedButton';
import { MarketplaceButton } from '@components/basics/MarketplaceButton';
import { Button } from '@components/main-components/button/Button';
import { Opensea20 } from '@components/main-components/media/icons/Opensea20';
import { useAuth } from '@context/auth/base';
import { trackClickEvent } from '@lib/analytics';
import { DataTestId } from '@utils/test';
import { Collect, ConnectWallet } from './shared';

export const OpenSeaButton = ({
  link,
  fullWidth,
  onClick,
  size = 'XL',
}: {
  link: string;
  fullWidth?: boolean;
  onClick?: () => void;
  size?: React.ComponentProps<typeof Button>['size'];
}) => (
  <Button
    data-testid={DataTestId.RELEASE_PAGE_OPENSEA_BUTTON}
    href={link}
    trailingIcon={
      <span className="[&_svg]:h-6 [&_svg]:w-6">
        <Opensea20 />
      </span>
    }
    variant="primary"
    fullWidth={fullWidth}
    label="Buy on OpenSea"
    size={size}
    onClick={onClick}
  />
);

const SoundMarketButton = ({
  link,
  fullWidth,
  onClick,
}: {
  link: string;
  fullWidth?: boolean;
  onClick?: () => void;
}) => (
  <MarketplaceButton
    label="Buy on Sound Market"
    link={link}
    fullWidth={fullWidth}
    onClick={onClick}
  />
);

/**
 * States reference for this component
 * - https://www.notion.so/soundxyz/Mint-Component-States-d808751d3f1f4fbd975a29da3aff704e
 * - https://www.figma.com/file/CfLYoS23bdpkE1z0X8cQXL/Song-Page-2.0?node-id=377%3A106192
 */
export const CollectButton = ({
  mintType,
  openseaCollectionUrl,
  marketPlaceUrl,
  onBuy,
  mintStarted = false,
  saleEnded = false,
  soldOut = false,
  loading = false,
  fullWidth = false,
  hasSaleAccess,
  publicMintStarted = false,
  hasNFT = false,
  areSchedulesLoading,
  artistId,
  artistUserId,
  releaseId,
  sourceComponent,
}: {
  mintType: MintingTypes;
  areSchedulesLoading: boolean;
  openseaCollectionUrl?: Maybe<string>;
  marketPlaceUrl?: Maybe<string>;
  onBuy: () => void;
  mintStarted?: boolean;
  saleEnded?: boolean;
  soldOut?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  hasSaleAccess?: boolean;
  publicMintStarted?: boolean;
  hasNFT?: boolean;
  artistId?: string;
  artistUserId?: string;
  releaseId: string;
  /** needed for tracking purposes to report what part of site we are triggering this  */
  sourceComponent: CollectButtonSource;
}) => {
  const { isAuthenticatedWithAPI } = useAuth();

  const isPublic =
    mintType === MintingTypes.FIXED ||
    mintType === MintingTypes.RANGE ||
    mintType === MintingTypes.OPEN_EDITION;

  if (soldOut && isPublic) {
    if (!!marketPlaceUrl) {
      return (
        <SoundMarketButton
          link={marketPlaceUrl}
          fullWidth={fullWidth}
          onClick={() => {
            trackClickEvent({
              event: 'Buy on Sound Market Clicked',
              properties: {
                associationId: releaseId,
                associationType: 'Release',
                sourceComponent,
              },
            });
          }}
        />
      );
    }
    if (!!openseaCollectionUrl) {
      return (
        <OpenSeaButton
          link={openseaCollectionUrl}
          fullWidth={fullWidth}
          onClick={() => {
            trackClickEvent({
              event: 'Buy on OpenSea Clicked',
              properties: {
                associationId: releaseId,
                associationType: 'Release',
                sourceComponent,
              },
            });
          }}
        />
      );
    }
    if (publicMintStarted && isPublic) {
      return <Collect disabled fullWidth={fullWidth} soldOut />;
    }
  }

  // Any sale which where mint has not started
  if (!mintStarted) {
    if (isAuthenticatedWithAPI) {
      if (!isPublic && hasSaleAccess) {
        return <Collect disabled fullWidth={fullWidth} />;
      } else {
        return !!artistId && !!artistUserId ? (
          <GetNotifiedButton type="mint_component" artistId={artistId} userId={artistUserId} dark />
        ) : null;
      }
    }

    // Users should be able to subscribe to public drops without connecting wallet
    if (isPublic && !!artistId && !!artistUserId) {
      return (
        <GetNotifiedButton type="mint_component" artistId={artistId} userId={artistUserId} dark />
      );
    }
  }

  // Presale is sold out
  if (!isPublic && soldOut) {
    if (!publicMintStarted && !!artistId && !!artistUserId) {
      return (
        <GetNotifiedButton type="mint_component" artistId={artistId} userId={artistUserId} dark />
      );
    }
    return null;
  }

  // Presale has ended
  if (!isPublic && saleEnded) {
    if (!publicMintStarted && !!artistId && !!artistUserId) {
      return (
        <GetNotifiedButton type="mint_component" artistId={artistId} userId={artistUserId} dark />
      );
    }
    return null;
  }

  if (isAuthenticatedWithAPI) {
    return (
      <>
        {hasSaleAccess ? (
          <Collect
            disabled={soldOut}
            loading={loading}
            fullWidth={fullWidth}
            onClick={() => {
              trackClickEvent({
                event: 'Collect Now Clicked',
                properties: {
                  associationId: releaseId,
                  associationType: 'Release',
                  sourceComponent,
                },
              });
              onBuy();
            }}
            variant={mintStarted ? 'fuchsia' : 'primary'}
          />
        ) : hasSaleAccess === undefined ? (
          <Collect
            disabled
            loading
            fullWidth={fullWidth}
            onClick={() => null}
            variant={mintStarted ? 'fuchsia' : 'primary'}
          />
        ) : !hasNFT && !!artistId && !!artistUserId ? (
          <GetNotifiedButton type="mint_component" artistId={artistId} userId={artistUserId} dark />
        ) : areSchedulesLoading ? (
          <Collect
            disabled
            loading
            fullWidth={fullWidth}
            onClick={() => null}
            variant={mintStarted ? 'fuchsia' : 'primary'}
          />
        ) : (
          <Button
            size="XL"
            label="Max editions collected"
            variant="primary"
            disabled
            fullWidth={fullWidth}
          />
        )}
      </>
    );
  }

  return <ConnectWallet fullWidth={fullWidth} sourceComponent={sourceComponent} />;
};
